import type { ConfigurationPort } from './configuration';
import type { MonitoringPort } from './contract';
import type { ForecastPort } from './forecasting';

export const RISK_ROOT = '/normal-operations/risk-warning';
export interface EvaluationCounts {
  areaId: string | null;
  generatedAt: string;
  pending: number;
  evaluated: number;
  unavailable: number;
}
export interface EvaluationCountsPort {
  read(areaId: string, signal: AbortSignal): Promise<EvaluationCounts>;
}
export interface RiskPageProps {
  readonly port: MonitoringPort;
  readonly configurationPort?: ConfigurationPort | undefined;
  readonly forecastPort?: ForecastPort | undefined;
  readonly countsPort?: EvaluationCountsPort | undefined;
  readonly areaId: string;
}
export type RiskRoute =
  | { kind: 'overview' | 'network' | 'evaluation'; areaId: string }
  | { kind: 'warning'; id: string; revision?: number; areaId: string }
  | { kind: 'forecast'; id: string; horizon?: number; areaId: string }
  | { kind: 'missing'; areaId: string };
export function riskHref(suffix = '', areaId = '', extra: Record<string, string> = {}): string {
  const params = new URLSearchParams(extra);
  if (areaId) params.set('areaId', areaId);
  return `${RISK_ROOT}${suffix}${params.size ? `?${params}` : ''}`;
}
export function parseRiskRoute(path: string, search = ''): RiskRoute {
  const params = new URLSearchParams(search);
  const areaId = params.get('areaId') ?? '';
  if (areaId && !/^[a-z][a-z0-9-]{0,63}$/.test(areaId)) return { kind: 'missing', areaId: '' };
  const suffix = path.replace(/\/$/, '').slice(RISK_ROOT.length);
  if (!path.startsWith(RISK_ROOT)) return { kind: 'missing', areaId };
  if (!suffix) return { kind: 'overview', areaId };
  if (suffix === '/network') return { kind: 'network', areaId };
  if (suffix === '/evaluation') return { kind: 'evaluation', areaId };
  const match = /^\/(warnings|forecasts)\/([^/]+)$/.exec(suffix);
  if (!match) return { kind: 'missing', areaId };
  let id: string;
  try {
    id = decodeURIComponent(match[2]!);
  } catch {
    return { kind: 'missing', areaId };
  }
  if (match[1] === 'forecasts') {
    if (!/^forecast-[a-f0-9]{64}$/.test(id)) return { kind: 'missing', areaId };
    const horizon = Number(params.get('horizon'));
    return { kind: 'forecast', id, areaId, ...([5, 10, 20].includes(horizon) ? { horizon } : {}) };
  }
  if (!/^[a-zA-Z0-9-]{1,128}$/.test(id)) return { kind: 'missing', areaId };
  const revision = Number(params.get('revision'));
  return {
    kind: 'warning',
    id,
    areaId,
    ...(Number.isSafeInteger(revision) && revision > 0 ? { revision } : {}),
  };
}
